/**
 * CSV attachment validation for the Copilot composer.
 *
 * Mirrors the rules Flow enforces server-side (`flow/backend/flow/files/service.py`)
 * so obviously invalid files are rejected before an upload round-trip:
 * `.csv` extension, ≤10 MB, ≤10,000 data rows, non-empty, UTF-8.
 */

import { BYTES_PER_MEGABYTE } from 'shared/copilot/utils/fileSize'

const CSV_EXTENSION = '.csv'

export const MAX_CSV_SIZE_BYTES = 10 * BYTES_PER_MEGABYTE
export const MAX_CSV_DATA_ROWS = 10_000

/** Interpolation values for `copilot.composer.csvAttachment.errors.*`, so the copy can never quote a stale limit. */
export const CSV_LIMIT_COPY_VALUES = {
  maxSizeMb: MAX_CSV_SIZE_BYTES / BYTES_PER_MEGABYTE,
  maxRows: MAX_CSV_DATA_ROWS
}

export const CSV_VALIDATION_REASON = {
  unsupportedType: 'unsupportedType',
  tooLarge: 'tooLarge',
  empty: 'empty',
  tooManyRows: 'tooManyRows',
  invalidEncoding: 'invalidEncoding'
} as const

export type CsvValidationReason = (typeof CSV_VALIDATION_REASON)[keyof typeof CSV_VALIDATION_REASON]

export type CsvValidationOutcome = { ok: true } | { ok: false; reason: CsvValidationReason }

const hasCsvExtension = (filename: string): boolean => filename.toLowerCase().endsWith(CSV_EXTENSION)

const validateCsvFileSync = (file: File): CsvValidationOutcome => {
  if (file.size === 0) {
    return { ok: false, reason: CSV_VALIDATION_REASON.empty }
  }
  if (!hasCsvExtension(file.name)) {
    return { ok: false, reason: CSV_VALIDATION_REASON.unsupportedType }
  }
  if (file.size > MAX_CSV_SIZE_BYTES) {
    return { ok: false, reason: CSV_VALIDATION_REASON.tooLarge }
  }
  return { ok: true }
}

interface CsvScanState {
  inQuotes: boolean
  recordHasContent: boolean
  sawHeader: boolean
  dataRowCount: number
  bomPending: boolean
}

const createScanState = (): CsvScanState => ({
  inQuotes: false,
  recordHasContent: false,
  sawHeader: false,
  dataRowCount: 0,
  bomPending: true
})

const completeRecord = (state: CsvScanState): 'ok' | 'too_many' => {
  if (!state.sawHeader) {
    state.sawHeader = true
    state.recordHasContent = false
    return 'ok'
  }
  if (state.recordHasContent) {
    state.dataRowCount += 1
    if (state.dataRowCount > MAX_CSV_DATA_ROWS) {
      return 'too_many'
    }
  }
  state.recordHasContent = false
  return 'ok'
}

const isLineBreak = (char: string): boolean => char === '\r' || char === '\n'

const skipBomIfPresent = (state: CsvScanState, text: string, index: number): { index: number; consumed: boolean } => {
  if (!state.bomPending) {
    return { index, consumed: false }
  }
  if (text.startsWith('\uFEFF', index)) {
    return { index: index + 1, consumed: true }
  }
  state.bomPending = false
  return { index, consumed: false }
}

const feedQuoteChar = (state: CsvScanState, text: string, index: number): number => {
  if (state.inQuotes && text[index + 1] === '"') {
    state.recordHasContent = true
    return index + 2
  }
  state.inQuotes = !state.inQuotes
  state.recordHasContent = true
  return index + 1
}

const feedLineBreak = (
  state: CsvScanState,
  text: string,
  index: number,
  char: string
): { nextIndex: number; status: 'ok' | 'too_many' } => {
  let nextIndex = index
  if (char === '\r' && text[index + 1] === '\n') {
    nextIndex += 1
  }
  nextIndex += 1
  return {
    nextIndex,
    status: completeRecord(state) === 'too_many' ? 'too_many' : 'ok'
  }
}

const feedCharacters = (state: CsvScanState, text: string): 'ok' | 'too_many' => {
  let index = 0
  while (index < text.length) {
    const bom = skipBomIfPresent(state, text, index)
    if (bom.consumed) {
      index = bom.index
      continue
    }
    index = bom.index

    const char = text[index]

    if (char === '"') {
      index = feedQuoteChar(state, text, index)
      continue
    }

    if (!state.inQuotes && isLineBreak(char)) {
      const lineBreak = feedLineBreak(state, text, index, char)
      if (lineBreak.status === 'too_many') {
        return 'too_many'
      }
      index = lineBreak.nextIndex
      continue
    }

    if (!isLineBreak(char)) {
      state.recordHasContent = true
    }
    index += 1
  }
  return 'ok'
}

const finalizeScan = (state: CsvScanState): 'ok' | 'too_many' => (state.recordHasContent ? completeRecord(state) : 'ok')

/** `FileReader.readAsArrayBuffer` wrapped as a promise — used instead of the
 * newer `File.arrayBuffer()`/`File.stream()` APIs, which aren't implemented
 * consistently across browsers/jsdom test environments. */
const readFileAsArrayBuffer = async (file: File): Promise<ArrayBuffer> =>
  await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      resolve(reader.result as ArrayBuffer)
    }
    reader.onerror = () => {
      reject(reader.error ?? new Error('Failed to read file'))
    }
    reader.readAsArrayBuffer(file)
  })

/**
 * Data-row count + UTF-8 validation. Files are capped at `MAX_CSV_SIZE_BYTES`
 * (10 MB) by the sync check that always runs first, so reading the whole
 * buffer at once is cheap.
 */
const countCsvDataRows = async (file: File): Promise<CsvValidationOutcome> => {
  const state = createScanState()
  const decoder = new TextDecoder('utf-8', { fatal: true })

  try {
    const bytes = new Uint8Array(await readFileAsArrayBuffer(file))
    const text = decoder.decode(bytes)

    if (feedCharacters(state, text) === 'too_many') {
      return { ok: false, reason: CSV_VALIDATION_REASON.tooManyRows }
    }
    if (finalizeScan(state) === 'too_many') {
      return { ok: false, reason: CSV_VALIDATION_REASON.tooManyRows }
    }

    return { ok: true }
  } catch {
    return { ok: false, reason: CSV_VALIDATION_REASON.invalidEncoding }
  }
}

/** Full client-side validation: extension/size checks first, then a streaming row-count/UTF-8 scan. */
export const validateCsvFile = async (file: File): Promise<CsvValidationOutcome> => {
  const syncOutcome = validateCsvFileSync(file)
  if (!syncOutcome.ok) {
    return syncOutcome
  }
  return await countCsvDataRows(file)
}

const CSV_MIME_TYPE = 'text/csv'

interface AttachCsvFileMetadata {
  gcs_uri?: string
  gcsUri?: string
  filename?: string
}

interface CsvAttachMessage {
  role: string
  content?: unknown
  csvAttachMode?: string
  attachCsvFile?: AttachCsvFileMetadata
}

const readGcsUri = (metadata: AttachCsvFileMetadata): string | null => {
  const gcsUri = metadata.gcs_uri ?? metadata.gcsUri
  return typeof gcsUri === 'string' && gcsUri.trim().length > 0 ? gcsUri.trim() : null
}

/**
 * Reads standalone CSV identity from hydrated AG-UI message metadata persisted
 * by Flow (`attachCsvFile` top-level or nested in content).
 */
export const readAttachCsvFileFromMessage = (
  message: CsvAttachMessage
): { gcsUri: string; filename: string } | null => {
  const candidates: Array<AttachCsvFileMetadata | undefined> = [message.attachCsvFile]
  if (message.content != null && typeof message.content === 'object' && !Array.isArray(message.content)) {
    const nested = (message.content as Record<string, unknown>).attachCsvFile
    if (nested != null && typeof nested === 'object') {
      candidates.push(nested as AttachCsvFileMetadata)
    }
  }

  for (const metadata of candidates) {
    if (metadata == null) {
      continue
    }
    const gcsUri = readGcsUri(metadata)
    const filename = typeof metadata.filename === 'string' ? metadata.filename.trim() : ''
    if (gcsUri != null && filename.length > 0) {
      return { gcsUri, filename }
    }
    if (gcsUri != null) {
      return { gcsUri, filename: filenameFromGcsUri(gcsUri) }
    }
  }

  return null
}

interface DocumentContentPart {
  type: 'document'
  source: { type: 'url' | 'data'; value: string; mimeType?: string }
}

const isDocumentPart = (part: unknown): part is DocumentContentPart =>
  part != null &&
  typeof part === 'object' &&
  (part as { type?: unknown }).type === 'document' &&
  typeof (part as { source?: unknown }).source === 'object'

const isTextPart = (part: unknown): part is { type: 'text'; text: string } =>
  part != null && typeof part === 'object' && (part as { type?: unknown }).type === 'text'

/** Best-effort filename from a GCS URI (Flow's own fallback when `file_data`
 * has no `filename`, see `flow/backend/flow/chat/csv_context.py`). */
const filenameFromGcsUri = (gcsUri: string): string => gcsUri.split('/').pop() ?? gcsUri

const looksLikeCsvFilename = (filename: string): boolean => filename.toLowerCase().endsWith(CSV_EXTENSION)

/**
 * Resolves a human-readable filename for a CSV attachment chip in message
 * history. Prefers the Files API index (`gcsUri → filename`); falls back to
 * the URI tail only when it looks like a real `.csv` name (not a Flow file id).
 */
export const resolveCsvDisplayFilename = (
  gcsUri: string,
  filenameByGcsUri?: Readonly<Record<string, string> | ReadonlyMap<string, string>>
): string | null => {
  if (filenameByGcsUri != null) {
    const fromIndex =
      filenameByGcsUri instanceof Map
        ? filenameByGcsUri.get(gcsUri)
        : (filenameByGcsUri as Readonly<Record<string, string>>)[gcsUri]
    if (fromIndex != null) {
      return fromIndex
    }
  }

  const uriTail = filenameFromGcsUri(gcsUri)
  return looksLikeCsvFilename(uriTail) ? uriTail : null
}

/**
 * Filename for agent.state `attach_csv_file` restore on chat hydration.
 * Always non-null: prefers the Files API index, else the GCS URI tail (including
 * Flow file ids when the index is unavailable).
 */
export const resolveSessionRestoreFilename = (
  gcsUri: string,
  filenameByGcsUri?: Readonly<Record<string, string>>
): string => {
  const fromIndex = resolveCsvDisplayFilename(gcsUri, filenameByGcsUri)
  if (fromIndex != null) {
    return fromIndex
  }
  return filenameFromGcsUri(gcsUri)
}

/**
 * Extracts the `text/csv` document part from a user message's `content`
 * (array form only — plain string content never carries an attachment).
 */
export const extractAttachedCsvFromUserContent = (content: unknown): { gcsUri: string; filename: string } | null => {
  if (!Array.isArray(content)) {
    return null
  }
  for (const part of content) {
    if (isDocumentPart(part) && part.source.type === 'url' && part.source.mimeType === CSV_MIME_TYPE) {
      return { gcsUri: part.source.value, filename: filenameFromGcsUri(part.source.value) }
    }
  }
  return null
}

/** Extracts the display text from a user message's `content` (string or multimodal array form). */
export const extractUserMessageText = (content: unknown): string => {
  if (typeof content === 'string') {
    return content
  }
  if (Array.isArray(content)) {
    const textPart = content.find(isTextPart)
    return textPart?.text ?? ''
  }
  return content == null ? '' : String(content)
}

/**
 * Client-side analog of Flow's `latest_attach_csv_from_messages`: scans
 * hydrated AG-UI messages in reverse for the most recent user message
 * carrying a `text/csv` document part, and returns its GCS URI.
 */
export const findLatestAttachedCsvUri = (
  messages: ReadonlyArray<{ role: string; content?: unknown }>
): string | null => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]
    if (message.role !== 'user') {
      continue
    }
    const attached = extractAttachedCsvFromUserContent(message.content)
    if (attached != null) {
      return attached.gcsUri
    }
  }
  return null
}

const isSupplementaryCsvMessage = (message: { content?: unknown; csvAttachMode?: string }): boolean => {
  if (message.csvAttachMode === 'supplementary') {
    return true
  }
  if (message.content != null && typeof message.content === 'object' && !Array.isArray(message.content)) {
    const mode = (message.content as Record<string, unknown>).csvAttachMode
    return mode === 'supplementary'
  }
  return false
}

export interface FindLatestStandaloneAttachedCsvUriOptions {
  /** When false, only `attachCsvFile` metadata counts (hydration from DB). Default true for live session. */
  allowDocumentPartFallback?: boolean
}

/**
 * Like `findLatestAttachedCsvUri`, but skips user messages sent as supplementary
 * CSV (platform datasource + per-turn attach) so standalone hydration does not
 * mis-route GA4+CSV chats to `csv_data_agent`.
 */
export const findLatestStandaloneAttachedCsvUri = (
  messages: readonly CsvAttachMessage[],
  options?: FindLatestStandaloneAttachedCsvUriOptions
): string | null => {
  const allowDocumentPartFallback = options?.allowDocumentPartFallback !== false

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]
    if (message.role !== 'user') {
      continue
    }
    if (isSupplementaryCsvMessage(message)) {
      continue
    }
    const metadataAttach = readAttachCsvFileFromMessage(message)
    if (metadataAttach != null) {
      return metadataAttach.gcsUri
    }
    if (!allowDocumentPartFallback) {
      continue
    }
    const attached = extractAttachedCsvFromUserContent(message.content)
    if (attached != null) {
      return attached.gcsUri
    }
  }
  return null
}
